import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  EditIcon,
  DeleteIcon,
  SearchIcon,
  CloseIcon,
  ChevronDownIcon,
  PrintIcon,
} from "../../../Icons";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { useGeneralFormsList } from "./hooks/useGeneralFormsList";
import Table from "../../../components/UI/Table";
import { displayDateUS } from "../../../utils/dateUtils";
import { sortClinicalFormsByScheduleDateAsc } from "../../Patients/components/clinicalForms/sortClinicalForms";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { useRowSelection } from "../../Documents/hooks/useRowSelection";
import GeneralFormsBulkActions from "./components/GeneralFormsBulkActions";
import { printGeneralForms } from "./print/generalFormsPrintService";
import { getVisibleGeneralForms } from "./generalFormsCatalog";
import ClinicalFormMarksRow from "../../Patients/components/clinicalForms/ClinicalFormMarksRow";
import { canManageGeneralFormFlags } from "../../../utils/formPermissions";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  PAGE_ACTIONS,
  CELL,
  CELL_MUTED,
  CELL_STRONG,
  MODULE_TAB_BTN,
} from "../../../ui/pageChrome";
import { APPLICATION_THEME } from "../applicationTheme";

/** Matches backend Daily Log / RouteSheetStatus workflow. */
const STATUS_FILTERS = [
  { value: "", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "send_to_office", label: "Sent to Office" },
  { value: "to_be_corrected", label: "To Be Corrected" },
  { value: "completed", label: "Completed" },
];

const STATUS_LABEL = {
  pending: "Pending",
  send_to_office: "Sent to Office",
  to_be_corrected: "To Be Corrected",
  completed: "Completed",
};

const STATUS_BADGE_CLASS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  send_to_office: "bg-sky-50 text-sky-700 border-sky-200",
  to_be_corrected: "bg-yellow-50 text-yellow-800 border-yellow-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const getRowKey = (row) => String(row?.id ?? "");

const GeneralFormsList = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useCurrentUser();
  const canManageFlags = canManageGeneralFormFlags(user);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [printLoading, setPrintLoading] = useState(false);
  const [flagLoading, setFlagLoading] = useState(false);

  const visibleTabs = useMemo(() => getVisibleGeneralForms(), []);
  const showTabs = visibleTabs.length > 1;

  const {
    activeTab,
    setActiveTab,
    activeForm,
    rows,
    loading,
    page,
    setPage,
    totalPages,
    total,
    limit,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    confirmDelete,
    handleDelete,
    applyMarks,
  } = useGeneralFormsList();

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (rows || []).filter((row) => {
      if (statusFilter) {
        const s = String(row.status || "pending").toLowerCase();
        const want = statusFilter.toLowerCase();
        const normalized = s === "draft" ? "pending" : s;
        if (normalized !== want) return false;
      }
      if (!q) return true;
      return (
        String(row.employeeName || row.filledByName || "")
          .toLowerCase()
          .includes(q) ||
        String(row.patientName || "")
          .toLowerCase()
          .includes(q) ||
        String(row.title || row.filledByRole || "")
          .toLowerCase()
          .includes(q) ||
        String(activeForm?.title || "")
          .toLowerCase()
          .includes(q)
      );
    });
    if (activeForm?.id !== "daily-log") return list;
    return sortClinicalFormsByScheduleDateAsc(
      list.map((row) => ({
        ...row,
        scheduleDate:
          row.scheduleDate ||
          row.serviceDate ||
          row.scheduledVisit?.visitDate ||
          null,
      })),
    );
  }, [rows, search, statusFilter, activeForm]);

  const selection = useRowSelection(filteredRows, getRowKey);

  useEffect(() => {
    selection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, search, statusFilter]);

  const hasActiveFilters = Boolean(search.trim() || statusFilter);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
  };

  const onEdit = useCallback(
    (row) => {
      if (!activeForm || row?.id == null) return;
      navigate(activeForm.editRoute(row.id), {
        state: { dailyLog: row },
      });
    },
    [activeForm, navigate],
  );

  const onDelete = useCallback(
    (id) => {
      confirmDelete(id, activeTab);
    },
    [confirmDelete, activeTab],
  );

  const onPrintRows = useCallback(
    async (rows) => {
      setPrintLoading(true);
      try {
        await printGeneralForms(rows, activeTab);
      } finally {
        setPrintLoading(false);
      }
    },
    [activeTab],
  );

  const onFlag = useCallback(
    async (markType, action) => {
      const ids = selection.selectedRows.map((row) => row.id);
      if (!ids.length) return;
      setFlagLoading(true);
      try {
        const result = await applyMarks(ids, markType, action);
        if (result) {
          toast.success(
            action === "remove"
              ? `Removed ${markType} from ${result.updatedCount || ids.length} form(s)`
              : `Marked ${result.updatedCount || ids.length} form(s) as ${markType}`,
          );
        }
      } catch (err) {
        toast.error(err.message || "Failed to update flags");
      } finally {
        setFlagLoading(false);
      }
    },
    [applyMarks, selection.selectedRows],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        header: "Form name",
        accessor: "formName",
        sortable: true,
        className: "pl-3 pr-3 w-[14%]",
        render: () => (
          <span className={`${CELL_STRONG} truncate block`}>
            {activeForm?.title || "—"}
          </span>
        ),
      },
      {
        header: "Patient Name",
        accessor: "patientName",
        sortable: true,
        className: "pl-3 pr-3 w-[16%]",
        render: (row) => (
          <span
            className={`${CELL_STRONG} truncate block`}
            title={
              row.patientId
                ? row.patientName || ""
                : row.patientName
                  ? `${row.patientName} (not linked)`
                  : ""
            }
          >
            {row.patientName || "—"}
            {!row.patientId && row.patientName ? (
              <span className="ml-1 text-[10px] font-medium text-slate-400">
                Other
              </span>
            ) : null}
          </span>
        ),
      },
      {
        header: activeForm?.id === "daily-log" ? "Schedule Date" : "Date",
        accessor: activeForm?.id === "daily-log" ? "scheduleDate" : "createdAt",
        sortable: true,
        className: "whitespace-nowrap pl-3 pr-3 w-[11%]",
        render: (row) => (
          <span className={`${CELL_MUTED} tabular-nums`}>
            {displayDateUS(
              activeForm?.id === "daily-log"
                ? row.scheduleDate ||
                    row.serviceDate ||
                    row.scheduledVisit?.visitDate ||
                    row.createdAt
                : row.createdAt,
            ) || "—"}
          </span>
        ),
      },
      {
        header: "Filled By",
        accessor: "employeeName",
        sortable: true,
        className: "pl-3 pr-3 w-[13%]",
        render: (row) => (
          <span className={`${CELL_STRONG} truncate block`}>
            {row.filledByName || row.employeeName || "—"}
          </span>
        ),
      },
      {
        header: "Role",
        accessor: "title",
        sortable: true,
        className: "pl-3 pr-3 w-[8%]",
        render: (row) => (
          <span className={`${CELL} truncate block`}>
            {row.filledByRole || row.title || "—"}
          </span>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        className: "whitespace-nowrap pl-3 pr-3 w-[10%]",
        render: (row) => {
          const raw = String(row.status || "pending").toLowerCase();
          const label = STATUS_LABEL[raw] || STATUS_LABEL.pending;
          const badgeClass =
            STATUS_BADGE_CLASS[raw] || STATUS_BADGE_CLASS.pending;
          return (
            <span
              className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold ${badgeClass}`}
            >
              {label}
            </span>
          );
        },
      },
      {
        header: "Flags",
        accessor: "marks",
        className: "whitespace-nowrap pl-3 pr-3 w-[12%]",
        render: (row) =>
          activeForm?.hasFlags ? (
            <ClinicalFormMarksRow
              marks={row.marks || []}
              size="sm"
              onRemoveMark={
                canManageFlags
                  ? async (markType) => {
                      try {
                        const result = await applyMarks(
                          [row.id],
                          markType,
                          "remove",
                        );
                        if (result) toast.success(`${markType} removed`);
                      } catch (err) {
                        toast.error(err.message || "Failed to remove flag");
                      }
                    }
                  : undefined
              }
            />
          ) : (
            <span className={CELL_MUTED}>—</span>
          ),
      },
      {
        header: "Actions",
        accessor: "actions",
        className: "whitespace-nowrap pl-3 pr-6 w-[16%] text-right",
        render: (row) => (
          <div className="inline-flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPrintRows([row]);
              }}
              className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded border transition-colors ${APPLICATION_THEME.button.outline}`}
              title="Print form"
              aria-label="Print form"
            >
              <PrintIcon size={13} />
              Print
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row);
              }}
              className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded border transition-colors ${APPLICATION_THEME.button.outline}`}
              title={
                !canManageFlags &&
                (row.status === "send_to_office" || row.status === "completed")
                  ? "View form"
                  : "Edit form"
              }
              aria-label={
                !canManageFlags &&
                (row.status === "send_to_office" || row.status === "completed")
                  ? "View form"
                  : "Edit form"
              }
            >
              <EditIcon size={13} />
              {!canManageFlags &&
              (row.status === "send_to_office" || row.status === "completed")
                ? "View"
                : "Edit"}
            </button>
            {isAdmin ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(row.id);
                }}
                className="inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded border border-red-200 text-red-700 bg-white hover:bg-red-50 transition-colors"
                title="Delete form"
                aria-label="Delete form"
              >
                <DeleteIcon size={13} />
                Delete
              </button>
            ) : null}
          </div>
        ),
      },
    ];

    return cols;
  }, [
    activeForm,
    applyMarks,
    canManageFlags,
    isAdmin,
    onDelete,
    onEdit,
    onPrintRows,
  ]);

  const tableSelection = useMemo(
    () => ({
      enabled: true,
      isSelected: (row) => selection.isSelected(getRowKey(row)),
      isSelectable: () => true,
      onToggleRow: (row, _index, checked) =>
        selection.toggleRow(getRowKey(row), checked),
      allSelected: selection.allSelectableSelected,
      someSelected: selection.someSelectableSelected,
      onToggleAll: selection.toggleAllSelectable,
    }),
    [selection],
  );

  const runPrint = async () => {
    await onPrintRows(selection.selectedRows);
  };

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={`text-2xl font-bold ${APPLICATION_THEME.section.title}`}>
            Submitted Forms
          </h1>
          <p className={PAGE_SUBTITLE}>
            {isAdmin
              ? "View and manage all submitted general forms"
              : "View and manage your submitted general forms"}
          </p>
        </div>
        <div className={PAGE_ACTIONS}>
          <button
            type="button"
            onClick={() => navigate("/general-forms/library")}
            className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors ${APPLICATION_THEME.button.primary}`}
          >
            <PlusIcon size={16} />
            New Form
          </button>
        </div>
      </div>

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${APPLICATION_THEME.section.wrap}`}
      >
        {showTabs ? (
          <nav
            className={`grid w-full border-b ${APPLICATION_THEME.section.head}`}
            style={{
              gridTemplateColumns: `repeat(${visibleTabs.length}, minmax(0, 1fr))`,
            }}
          >
            {visibleTabs.map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id);
                    clearFilters();
                  }}
                  className={`${MODULE_TAB_BTN} border-indigo-100 ${
                    active
                      ? APPLICATION_THEME.tab.active
                      : APPLICATION_THEME.tab.inactive
                  }`}
                >
                  <span className="truncate">{tab.label}</span>
                  {active ? (
                    <span
                      className={`absolute left-0 right-0 bottom-0 h-0.5 ${APPLICATION_THEME.tab.underline}`}
                    />
                  ) : null}
                </button>
              );
            })}
          </nav>
        ) : (
          <div
            className={`flex items-center h-11 px-4 border-b ${APPLICATION_THEME.section.head}`}
          >
            <h2
              className={`text-xs font-semibold uppercase tracking-wide ${APPLICATION_THEME.section.title}`}
            >
              {activeForm?.label || "Forms"}
            </h2>
          </div>
        )}

        <div className="px-3 pt-3">
          <GeneralFormsBulkActions
            selectedCount={selection.selectedCount}
            selectedRows={selection.selectedRows}
            loading={printLoading || flagLoading}
            hasFlags={Boolean(activeForm?.hasFlags)}
            canManageFlags={canManageFlags}
            onPrint={runPrint}
            onApplyMark={(markType) => onFlag(markType, "apply")}
            onRemoveMark={(markType) => onFlag(markType, "remove")}
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by employee, role, or patient..."
              className={`w-full h-8 pl-9 pr-8 text-sm bg-white border border-indigo-200 rounded-md text-slate-800 placeholder:text-slate-400 outline-none transition-colors ${APPLICATION_THEME.fieldFocus}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="relative">
              <label className="sr-only" htmlFor="general-forms-status-filter">
                Filter by status
              </label>
              <select
                id="general-forms-status-filter"
                className={`appearance-none h-8 min-w-[130px] pl-3 pr-8 text-sm rounded-md border outline-none transition-colors cursor-pointer bg-white ${APPLICATION_THEME.fieldFocus} ${
                  statusFilter
                    ? "border-indigo-400 text-indigo-700 font-medium bg-indigo-50/60"
                    : "border-indigo-200 text-slate-700"
                }`}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTERS.map((option) => (
                  <option
                    key={option.value || "all-status"}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className="h-8 px-2.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50 rounded-md border border-indigo-200 transition-colors whitespace-nowrap"
              >
                Clear
              </button>
            ) : null}

            <span
              className={`h-8 inline-flex items-center px-2.5 rounded-md text-[11px] font-medium tabular-nums ${APPLICATION_THEME.section.badge}`}
            >
              {filteredRows.length}
              {hasActiveFilters ? ` / ${total}` : ""} forms
            </span>
          </div>
        </div>

        <Table
          columns={columns}
          data={filteredRows}
          loading={loading}
          keyField="id"
          showSrNo
          density="compact"
          embedded
          headTone={APPLICATION_THEME.tableHead}
          tableClassName="table-fixed"
          selection={tableSelection}
          pagination={{
            currentPage: page,
            totalPages,
            onPageChange: setPage,
            totalItems: total,
            itemsPerPage: limit,
          }}
          noDataMessage="No forms found."
        />
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Form"
        message="Are you sure you want to delete this form? This action cannot be undone."
        isDanger={true}
        confirmText="Delete"
      />
    </div>
  );
};

export default GeneralFormsList;
